import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { toast } from 'react-toastify';
import PropTypes from "prop-types";

const AssignTemplatesToClient = ({ isOpen, onClose, onSuccess }) => {
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState({});
  const [assignments, setAssignments] = useState({});
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsRes, templatesRes, collabRes] = await Promise.all([
        api.get('/clients'),
        api.get('/tasks/templates?active=true'),
        api.get('/users?role=collaborator'),
      ]);

      const allTemplates = Array.isArray(templatesRes.data) ? templatesRes.data : [];
      const globalTemplates = allTemplates.filter((t) => {
        const cid = t?.clientId;
        // global si null/undefined
        if (cid === null || typeof cid === 'undefined') return true;
        // si string ou objet => lié à un client
        return false;
      });

      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setTemplates(globalTemplates);
      setCollaborators(Array.isArray(collabRes.data) ? collabRes.data : []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateToggle = (templateId) => {
    setSelectedTemplates((prev) => ({
      ...prev,
      [templateId]: !prev[templateId],
    }));

    // Auto-assign par défaut si template a un defaultAssignee
    const template = templates.find((t) => t._id === templateId);
    if (template && template.defaultAssignee && !assignments[templateId]) {
      setAssignments((prev) => ({
        ...prev,
        [templateId]: template.defaultAssignee._id,
      }));
    }
  };

  const handleAssigneeChange = (templateId, userId) => {
    setAssignments((prev) => ({ ...prev, [templateId]: userId }));
  };

  const handleAssignTemplates = async () => {
    if (!selectedClient) {
      toast.error('Sélectionnez un client');
      return;
    }

    const checkedTemplateIds = Object.keys(selectedTemplates).filter((id) => selectedTemplates[id]);
    if (checkedTemplateIds.length === 0) {
      toast.error('Sélectionnez au moins un template');
      return;
    }

    // Vérifier que tous les templates cochés ont un assigné
    for (const templateId of checkedTemplateIds) {
      if (!assignments[templateId]) {
        toast.error(`Un template n'a pas d'assigné défini`);
        return;
      }
    }

    setAssigning(true);
    try {
      const payload = {
        clientId: selectedClient,
        templateIds: checkedTemplateIds,
        assignments,
        overrides: {}, // optionnel pour plus tard
      };

      const response = await api.post('/tasks/templates/bulk-assign', payload);

      toast.success(`${response.data.count} templates programmés avec succès !`);

      setSelectedClient('');
      setSelectedTemplates({});
      setAssignments({});

      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      console.error('Erreur programmation templates:', error);
      const errorMsg =
        error.response?.data?.error || 'Erreur lors de la programmation des templates';
      toast.error(errorMsg);
    } finally {
      setAssigning(false);
    }
  };

  const handleReset = () => {
    setSelectedClient('');
    setSelectedTemplates({});
    setAssignments({});
  };

  if (!isOpen) return null;

  const selectedClientObj = clients.find((c) => c._id === selectedClient);
  const checkedCount = Object.values(selectedTemplates).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-center border-b">
          <div>
            <h2 className="text-xl font-bold">Programmer des templates</h2>
            <p className="text-blue-100 text-sm">
              Assigne des templates globaux à un client (création de templates client-spécifiques).
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-blue-800 p-2 rounded-full transition">
            ✕
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Chargement des données...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* 1. Client */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                1. Sélectionner un client
              </label>
              <select
                value={selectedClient}
                onChange={(e) => {
                  setSelectedClient(e.target.value);
                  setSelectedTemplates({});
                  setAssignments({});
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Sélectionner un client --</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.companyName || client.name || 'Client sans nom'}
                  </option>
                ))}
              </select>

              {selectedClientObj && (
                <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200 text-sm text-blue-700">
                  <strong>Client sélectionné:</strong> {selectedClientObj.companyName || selectedClientObj.name}
                </div>
              )}
            </div>

            {/* 2. Templates */}
            {selectedClient && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  2. Sélectionner templates ({checkedCount} sélectionné(s))
                </label>

                {templates.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">
                    Aucun template global actif disponible.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {templates.map((template) => {
                      const isChecked = !!selectedTemplates[template._id];
                      const assignee = assignments[template._id];
                      const assigneeObj = collaborators.find((c) => c._id === assignee);

                      return (
                        <div
                          key={template._id}
                          className={`p-3 border rounded-lg transition ${
                            isChecked
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleTemplateToggle(template._id)}
                              className="mt-1 w-5 h-5 text-blue-600 rounded cursor-pointer"
                            />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 truncate">
                                  {template.defaultTitle || template.name}
                                </h4>
                                <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">
                                  {template.category || 'N/A'}
                                </span>
                              </div>

                              {template.defaultDescription && (
                                <p className="text-xs text-gray-600 mb-2 line-clamp-1">
                                  {template.defaultDescription}
                                </p>
                              )}

                              {isChecked && (
                                <div className="mt-2 flex gap-2 items-center">
                                  <label className="text-xs font-semibold text-gray-700">
                                    Assigner:
                                  </label>
                                  <select
                                    value={assignee || ''}
                                    onChange={(e) =>
                                      handleAssigneeChange(template._id, e.target.value)
                                    }
                                    className="flex-1 p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                  >
                                    <option value="">-- Sélectionner --</option>

                                    {template.defaultAssignee && (
                                      <option value={template.defaultAssignee._id}>
                                        {template.defaultAssignee.name || 'Par défaut'}
                                      </option>
                                    )}

                                    {collaborators.map((collab) => (
                                      <option key={collab._id} value={collab._id}>
                                        {collab.name || collab.email}
                                      </option>
                                    ))}
                                  </select>

                                  {assigneeObj && (
                                    <span className="text-xs text-blue-600 font-medium">
                                      {assigneeObj.name}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 3. Résumé */}
            {selectedClient && checkedCount > 0 && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-green-700">
                  <strong>Prêt à programmer</strong>: {checkedCount} template(s) pour{' '}
                  <strong>{selectedClientObj?.companyName || 'ce client'}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-100 border-t p-4 flex justify-end gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition font-medium"
            disabled={assigning}
          >
            Réinitialiser
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition font-medium"
            disabled={assigning}
          >
            Annuler
          </button>
          <button
            onClick={handleAssignTemplates}
            disabled={!selectedClient || checkedCount === 0 || assigning}
            className={`px-6 py-2 rounded-lg font-medium transition text-white flex items-center gap-2 ${
              !selectedClient || checkedCount === 0 || assigning
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {assigning ? 'Programmation...' : `Programmer (${checkedCount})`}
          </button>
        </div>
      </div>
    </div>
  );
};

/** ✅ Ajout seulement: validation des props (corrige react/prop-types + no-unused-vars) */
AssignTemplatesToClient.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func, // optionnel
};

AssignTemplatesToClient.defaultProps = {
  onSuccess: undefined,
};

export default AssignTemplatesToClient;
